/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2024 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

import { HTMLAttributes, useEffect, useState } from 'preact/compat';
import { Container, toLanguageTag } from '@adobe-commerce/elsie/lib';
import { Price, PriceRange } from '@/pdp/components';
import PriceTiers from '@/pdp/components/PriceTiers';
import { events } from '@adobe-commerce/event-bus';
import { ProductModel } from '@/pdp/data/models';
import { config, getFetchedProductData } from '@/pdp/api';

export interface ProductPriceProps extends HTMLAttributes<HTMLDivElement> {
  scope?: string;
}

export const ProductPrice: Container<
  ProductPriceProps,
  ProductModel | null
> = ({ initialData = null, scope, children, ...props }) => {
  const { globalLocale = 'en-US' } = config.getConfig();

  const [data, setData] = useState<ProductModel | null>(initialData);

  const [locale, setLocale] = useState<string>(globalLocale);

  useEffect(() => {
    const dataEvent = events.on('pdp/data', setData, { scope });
    const localeEvent = events.on('locale', setLocale);

    return () => {
      dataEvent?.off();
      localeEvent?.off();
    };
  }, [scope]);

  if (!data || !data.prices) return null;

  return (
    <Price
      {...props}
      specialPrice={
        data.prices?.visible ? (
          <PriceRange {...data.prices?.final} locale={toLanguageTag(locale)} />
        ) : undefined
      }
      price={
        data.prices?.visible &&
        data.prices?.regular &&
        data.prices?.final.amount !== data.prices?.regular.amount ? (
          <PriceRange
            {...data.prices?.regular}
            locale={toLanguageTag(locale)}
          />
        ) : undefined
      }
      tiers={
        data.prices?.visible && data.prices?.tiers?.length > 0 ? (
          <PriceTiers
            tiers={data.prices.tiers}
            locale={toLanguageTag(locale)}
            regularPrice={data.prices.regular?.amount}
          />
        ) : undefined
      }
    />
  );
};

ProductPrice.getInitialData = getFetchedProductData;
